import { useTranslation } from 'react-i18next';
import { useState, useRef, useCallback, useEffect, startTransition } from 'react';
import { API } from '../../../API';

import { Collapsible } from './Collapsible';
import { PlatformChooser } from './PlatformChooser';
import { ReactComponent as FilterIcon } from '../assets/filter.svg';
import { DoubleRangeSlider } from './DoubleRangeSlider/DoubleRangeSlider';
import { CountryChooser } from "./CountryChooser";
import { StatusChooser } from './StatusChooser';
import { IoCChooser } from './IoCChooser';
import { AffiliationTypesChooser } from './AffiliationTypesChooser';
import { BotsChooser } from './BotsChooser';
import { TagsChooser } from './TagsChooser';
import {useClickOutside} from '../../../utils/useClickOutside.jsx'
import {getSelectedCountries} from '../../../utils/countries';
import { showRequestError } from '../../../utils/requestError';
import { AffiliationCountries } from '../../SearchPage/Filter/AffiliationCountries';
import { ExcludeSwitch } from '../../SearchPage/Filter/ExcludeSwitch';
import './Filter.scss';
import styles from './Filter.module.scss';
import {useCurrentUser} from "../../../contexts/CurrentUser";
import { GeoGroupFilter } from '../../SearchPage/Filter/GeoGroupFilter';


export const Filter = ({onChange, state}) => {
  const { t } = useTranslation();
  const clickRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const [platforms, setPlatforms] = useState(state.platforms || []);
  const [countries, setCountries] = useState(state.countries || []);
  const [statuses, setStatuses] = useState(state.status || []);
  const [audienceRange, setAudienceRange] = useState([state.audience_gte, state.audience_lte]);
  const [affiliationCountries, setAffiliationCountries] = useState(state.affiliationCountries || []);
  const [IoCs, setIoCs] = useState(state.IoCs || []);
  const [showOnlyBots, setShowOnlyBots] = useState(state.showOnlyBots || false);
  const [sourceTags, setSourceTags] = useState(state.sourceTags || []);
  const [isPlatformsOpen, setIsPlatformsOpen] = useState(false);
  const [isCountriesOpen, setIsCountriesOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const [isAffiliationCountriesOpen, setIsAffiliationCountriesOpen] = useState(false);
  const [isIoCsOpen, setIsIoCsOpen] = useState(false);
  const [isShowOnlyBotsOpen, setIsShowOnlyBotsOpen] = useState(false);
  const [isSourceTagsOpen, setIsSourceTagsOpen] = useState(false);
  const [geoGroup, setGeoGroup] = useState(state.geoGroup || []);
  const [geoGroupExclude, setGeoGroupExclude] = useState(state.geoGroupExclude || false);
  const [isGeoGroupOpen, setIsGeoGroupOpen] = useState(false);
  const [botsExclude, setBotsExclude] = useState(state.botsExclude || false);
  const [IoCsExclude, setIoCsExclude] = useState(state.IoCsExclude || false);
  const [sourceTagsExclude, setSourceTagsExclude] = useState(state.sourceTagsExclude || false);
  const [affiliationTypes, setAffiliationTypes] = useState(state.affiliationTypes || []);
  const [affiliationTypesExclude, setAffiliationTypesExclude] = useState(state.affiliationTypesExclude || false);
  const [isAffiliationTypesOpen, setIsAffiliationTypesOpen] = useState(false);

  const [currentUser] = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showTagsFilter = featureFlags?.includes('show_tags_filter');
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  const showCountryGroupsFilter = featureFlags?.includes('country_groups_filter');

  const [countriesExclude, setCountriesExclude] = useState(state.countriesExclude || false);
  const [affiliationCountriesExclude, setAffiliationCountriesExclude] = useState(state.affiliationCountriesExclude || false);
  const [audienceExclude, setAudienceExclude] = useState(state.audienceExclude || false);
  const [allCountries, setAllCountries] = useState(null);

  useEffect(() => {
    setPlatforms(state.platforms || []);
    setCountries(state.countries || []);
    setStatuses(state.status || []);
    setAudienceRange([state.audience_gte, state.audience_lte]);
    setAffiliationCountries(state.affiliationCountries || []);
    setIoCs(state.IoCs || []);
    setShowOnlyBots(state.showOnlyBots || false);
    setSourceTags(state.sourceTags || []);
    setCountriesExclude(state.countriesExclude || false);
    setAffiliationCountriesExclude(state.affiliationCountriesExclude || false);
    setAudienceExclude(state.audienceExclude || false);
    setGeoGroup(state.geoGroup || []);
    setGeoGroupExclude(state.geoGroupExclude || false);
    setBotsExclude(state.botsExclude || false);
    setIoCsExclude(state.IoCsExclude || false);
    setSourceTagsExclude(state.sourceTagsExclude || false);
    setAffiliationTypes(state.affiliationTypes || []);
    setAffiliationTypesExclude(state.affiliationTypesExclude || false);
  }, [state]);
  
  let filtersSumm = platforms.length + countries.length + statuses.length + affiliationCountries.length + affiliationTypes.length + IoCs.length + (showOnlyBots || botsExclude ? 1 : 0) + sourceTags.length + (geoGroup.length > 0 ? 1 : 0) + ((audienceRange[0] && audienceRange[0] !== 0) || (audienceRange[1] && audienceRange[1] !== 1000000000) ? 1 : 0);

  const getActiveExclusiveFilter = () => {
    if (affiliationCountries.length > 0 || affiliationTypes.length > 0) return 'State-affiliated';
    if (IoCs.length > 0) return 'High risk accounts';
    if (showOnlyBots === true && botsExclude === false) return 'Show only inauthentic actors'; // Only when showing ONLY bots
    return null;
  };

  const activeExclusiveFilter = getActiveExclusiveFilter();

  const isFilterDisabled = (filterName) => {
    return activeExclusiveFilter && activeExclusiveFilter !== filterName;
  };

  const getDisabledTooltip = (filterName) => {
    if (!isFilterDisabled(filterName)) return null;
    return t(`This filter can't be combined with '{{activeFilter}}'`, { activeFilter: t(activeExclusiveFilter) });
  };

  const getAllCountries = useCallback(() => {
    API.fetch('GET', `/API/v1/countries`).then((data) => {
      setAllCountries(data);
    }).catch((e) => showRequestError(e, t('Countries')));
  }, []);

  useEffect(getAllCountries, [getAllCountries]);

  const createContriesAndAffiliationCountriesOptions = (data) => {
    let selected = getSelectedCountries(countries, data?.objects);
      setCountries(selected);

    if(affiliationCountries?.length > 0 && affiliationCountries[0] === 'all') {
      setAffiliationCountries([{value: 'all', label: t('Select all')}])
    } else if (affiliationCountries?.length > 0) {
      let selectedAffiliationCountries = getSelectedCountries(affiliationCountries, data?.objects);
      setAffiliationCountries(selectedAffiliationCountries);
    }
  }

  useEffect(() => {
    if(allCountries){
      createContriesAndAffiliationCountriesOptions(allCountries)
    }
  }, [allCountries, state]);

  useClickOutside(clickRef, () => {
    if(isActive) {
      setIsActive(false)
    }
  })

  return (
    <div ref={clickRef} className={styles.root} translate='no'>
      <button 
        className={isActive ? `${styles.button} ${styles.filters} ${styles.active}` : `${styles.button} ${styles.filters}`}
        type='button'
        onClick={() => setIsActive(!isActive)}
      >
        {filtersSumm > 0 ? <span className='summ'>{filtersSumm }</span> : ''}
        {t('Filters')}
        <FilterIcon className={isActive ? 'iconActive' : 'icon'}/>
      </button>

      <div className={isActive ? `${styles.container} ${styles.show}` : `${styles.container}`}>
        <div className={styles.header}>
          <span>{t('Filter by')}</span>

          <span onClick={()=> {
            if (!isPlatformsOpen || !isCountriesOpen || !isAudienceOpen || !isStatusOpen || !isAffiliationCountriesOpen || !isAffiliationTypesOpen || !isIoCsOpen || !isShowOnlyBotsOpen || !isSourceTagsOpen || !isGeoGroupOpen) {
              setIsPlatformsOpen(true);
              setIsCountriesOpen(true);
              setIsAudienceOpen(true);
              setIsStatusOpen(true);
              setIsAffiliationCountriesOpen(true);
              setIsAffiliationTypesOpen(true);
              setIsIoCsOpen(true);
              setIsShowOnlyBotsOpen(true);
              setIsSourceTagsOpen(true);
              setIsGeoGroupOpen(true);
            } else {
              setIsPlatformsOpen(false);
              setIsCountriesOpen(false);
              setIsAudienceOpen(false);
              setIsStatusOpen(false);
              setIsAffiliationCountriesOpen(false);
              setIsAffiliationTypesOpen(false);
              setIsIoCsOpen(false);
              setIsShowOnlyBotsOpen(false);
              setIsSourceTagsOpen(false);
              setIsGeoGroupOpen(false);
            }
          }}>
            {isPlatformsOpen && isCountriesOpen && isAudienceOpen && isStatusOpen && isAffiliationCountriesOpen && isAffiliationTypesOpen && isIoCsOpen && isShowOnlyBotsOpen && isSourceTagsOpen && isGeoGroupOpen ? `${t('Minimize all')}` : `${t('Expand all')}`}
          </span>
        </div>

        <div className={styles.collapsiblesWrapper}>

          <Collapsible 
            name='Platforms'
            summ={platforms.length}
            isOpen={isPlatformsOpen}
            onChange={(open) => setIsPlatformsOpen(open)}
          >
            <PlatformChooser 
              selected={platforms}
              onChange={(checklist) => setPlatforms(checklist)}
            />
          </Collapsible>

          <Collapsible 
            name='Audience'
            isOpen={isAudienceOpen}
            onChange={(open) => setIsAudienceOpen(open)}
            exclude={audienceExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='audience-switch'
                value={audienceExclude}
                onChange={setAudienceExclude}
                color='red'
              />
            </div>
            <DoubleRangeSlider
              min={0}
              max={1000000000}
              step={1}
              audienceRange={audienceRange}
              onChange={(range) => setAudienceRange(range)}

            />
          </Collapsible>

          {showCountryGroupsFilter ? <Collapsible
            name='Actor origin country groups'
            summ={geoGroup?.length}
            isOpen={isGeoGroupOpen}
            onChange={(open) => setIsGeoGroupOpen(open)}
            exclude={geoGroupExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='geo-switch'
                value={geoGroupExclude}
                onChange={setGeoGroupExclude}
                color='red'
              />
            </div>
            <GeoGroupFilter
              selected={geoGroup}
              disabled={false}
              onChange={(checklist) => setGeoGroup(checklist)}
            />
          </Collapsible> : ''}

          <Collapsible
            name='Actor origin countries'
            summ={countries.length}
            isOpen={isCountriesOpen}
            onChange={(open) => setIsCountriesOpen(open)}
            exclude={countriesExclude}
          >
           <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='country-switch'
                value={countriesExclude}
                onChange={setCountriesExclude}
                color='red'
              />
            </div>
            <CountryChooser
              selected={countries}
              onChange={(checklist) => setCountries(checklist)}
              onClose={() => startTransition(() => setIsActive(true))}
            />
          </Collapsible>

          <Collapsible
            name='State-affiliated'
            summ={affiliationCountries.length}
            isOpen={isAffiliationCountriesOpen}
            onChange={(open) => setIsAffiliationCountriesOpen(open)}
            exclude={affiliationCountriesExclude}
            disabled={isFilterDisabled('State-affiliated')}
            tooltipText={getDisabledTooltip('State-affiliated')}
          >
            <AffiliationCountries
              selected={affiliationCountries}
              onChange={(value) => setAffiliationCountries(value)}
              onClose={() => startTransition(() => setIsActive(true))}
              onExcludeChange={(bool) => setAffiliationCountriesExclude(bool)}
              excludeValue={affiliationCountriesExclude}
            />

          </Collapsible>

          <Collapsible
            name='Affiliation Category'
            summ={affiliationTypes.length}
            isOpen={isAffiliationTypesOpen}
            onChange={(open) => setIsAffiliationTypesOpen(open)}
            exclude={affiliationTypesExclude}
            disabled={isFilterDisabled('State-affiliated')}
            tooltipText={getDisabledTooltip('State-affiliated')}
          >
            <AffiliationTypesChooser
              selected={affiliationTypes}
              onChange={(checklist) => setAffiliationTypes(checklist)}
              excludeValue={affiliationTypesExclude}
              onExcludeChange={setAffiliationTypesExclude}
              isDisabled={isFilterDisabled('State-affiliated')}
            />
          </Collapsible>

          {hideRiskActors ? '' : <Collapsible
            name='High risk accounts'
            summ={IoCs.length}
            isOpen={isIoCsOpen}
            onChange={(open) => setIsIoCsOpen(open)}
            exclude={IoCsExclude}
            disabled={isFilterDisabled('High risk accounts')}
            tooltipText={getDisabledTooltip('High risk accounts')}
          >
            <IoCChooser
              selected={IoCs}
              onChange={(checklist) => setIoCs(checklist)}
              excludeValue={IoCsExclude}
              onExcludeChange={setIoCsExclude}
              isDisabled={isFilterDisabled('High risk accounts')}
            />
          </Collapsible>}

          <Collapsible
            name='Inauthentic behavior'
            isOpen={isShowOnlyBotsOpen}
            summ={showOnlyBots === true ? 1 : botsExclude === true ? 1 : 0}
            exclude={botsExclude}
            onChange={(open) => setIsShowOnlyBotsOpen(open)}
          >
            <BotsChooser
              value={showOnlyBots === true && botsExclude === false ? true : botsExclude === true ? false : null}
              disableShowOnly={isFilterDisabled('Show only inauthentic actors')}
              onChange={(value) => {
                if(value) {
                  setShowOnlyBots(true);
                  setBotsExclude(false);
                }
                if(value === false) {
                  setBotsExclude(true);
                  setShowOnlyBots(true);
                }
                if(value === null) {
                  setShowOnlyBots(false);
                  setBotsExclude(false);
                }
              }}
            />
          </Collapsible>

          <Collapsible 
            name='Status'
            summ={statuses.length}
            isOpen={isStatusOpen}
            onChange={(open) => setIsStatusOpen(open)}
          >
            <StatusChooser
              selected={statuses}
              onChange={(checklist) => setStatuses(checklist)}
              onClose={() => setIsActive(true)}
            />
          </Collapsible>

          { showTagsFilter ? <Collapsible
            name='Actor tags'
            summ={sourceTags.length}
            isOpen={isSourceTagsOpen}
            onChange={(open) => setIsSourceTagsOpen(open)}
            exclude={sourceTagsExclude}
          >
            <TagsChooser
              selected={sourceTags}
              onChange={(value) => setSourceTags(value)}
              exclude={sourceTagsExclude}
              onExcludeChange={setSourceTagsExclude}
            />
          </Collapsible> : '' }
        </div>

        <div className={styles.controls}>
          <button 
            className={`${styles.button} ${styles.apply}`}
            type='button'
            onClick={() => {
              onChange(
                platforms,
                countries.map(country => country.value),
                audienceRange,
                statuses,
                affiliationCountries.map(country => country.value),
                IoCs,
                showOnlyBots,
                sourceTags,
                affiliationCountriesExclude,
                countriesExclude,
                audienceExclude,
                geoGroup,
                geoGroupExclude,
                botsExclude,
                IoCsExclude,
                sourceTagsExclude,
                affiliationTypes,
                affiliationTypesExclude,
              )
              setIsActive(false)
            }}
          >
            {t('Apply')}
          </button>

          <button 
            type='button'
            className={`${styles.button} ${styles.reset}`}
            onClick={() => {
              setPlatforms([]);
              setStatuses([]);
              setAudienceRange([0, 1000000000]);
              setCountries([]);
              setAffiliationCountries([]);
              setIoCs([]);
              setShowOnlyBots(false);
              setSourceTags([]);
              setAffiliationCountriesExclude(false);
              setCountriesExclude(false);
              setAudienceExclude(false);
              setGeoGroup([]);
              setGeoGroupExclude(false);
              setBotsExclude(false);
              setIoCsExclude(false);
              setSourceTagsExclude(false);
              setAffiliationTypes([]);
              setAffiliationTypesExclude(false);
            }}
          >
            {t('Reset all')}
          </button>
        </div>
      </div>


    </div>
  )
}